<script>
  import "sequential-workflow-designer/css/designer.css";
  import "sequential-workflow-designer/css/designer-light.css";
  import "sequential-workflow-designer/css/designer-dark.css";

  import { onMount, onDestroy } from "svelte";
  import {
    toolbar,
    workflow as workflowStore,
    workflowParents,
    initializeStore,
    luaCode,
    execution,
    definitions,
    filetree,
    jobs,
    ai,
    codegenError,
    setCodegenError,
    clearCodegenError,
    stepWarnings,
    setStepWarnings,
    currentStep,
    codeViewJump,
    viewInCode,
  } from "./Automator/store";
  import { Icon, LightBulb, Sparkles, ExclamationCircle } from "svelte-hero-icons";
  import MenubarWorkflow from "./Automator/MenubarWorkflow.svelte";
  import SidebarWorkflow from "./Automator/SidebarWorkflow.svelte";
  import SequentialWorkflowDesigner from "./Automator/SequentialWorkflowDesigner.svelte";
  import CodeInput from "./Inputs/CodeInput.svelte";
  import { cx } from "$utils/cx";
  import { designerControl } from "$utils/designer-control";
  import { persisted } from "$lib/utils/persisted-stores";
  import { processEventBatch } from "$utils/events";
  import { nonReactiveVariable } from "./utils/svelte";
  import StatusBanner from "./Automator/Workflow/StatusBanner.svelte";
  import LuaError from "./Automator/Workflow/LuaError.svelte";
  import PropTable from "./Automator/PropTable.svelte";
  import AIOverlay from "./Automator/AI/AIOverlay.svelte";
  import AISidebar from "./Automator/AI/AISidebar.svelte";

  export let code;
  export let job;
  export let workflow;
  export let workflow_parents = [];
  export let active_workflow_id;
  export let ui_assets;
  export let toolbox;
  export let root_definition;
  export let project_id;
  export let project_name;
  export let main_script_id;
  export let live;
  export let hidden = false;
  export let is_readonly = false;
  export let mock_env = false;
  export let tracing = false;
  export let debugging = false;
  export let user_roles = ["user"];
  export let user_nickname;
  export let tree_items = [];
  export let feature_flags = {};

  // Non-reactive value to track if the component is mounted
  const isMounted = nonReactiveVariable(false);
  const alwaysReadwriteIfPossible = persisted("always_readwrite", false);
  const autoRezoom = persisted("auto_rezoom", false);

  // TODO: Event driven vs server driven
  $: execution.mode.set(tracing || debugging ? "in_progress" : "idle");
  $: luaCode.set(code);

  // When a new codegen error arrives and nothing is selected, open the offending
  // step's editor so the error surfaces in place. Keep it conservative: never
  // yank selection away from a step the user is actively editing.
  let lastCodegenErrorId = null;
  $: {
    const errorId = $codegenError?.id ?? null;
    if (errorId !== lastCodegenErrorId) {
      lastCodegenErrorId = errorId;
      if (errorId && !$currentStep) {
        execution.setCurrentStep(errorId);
      }
    }
  }

  function openErrorStep() {
    if (!$codegenError?.id) return;
    execution.setCurrentStep($codegenError.id);
    $toolbar.show_prop_pane = true;
  }
  $: {
    definitions.reset();
    definitions.initialize(toolbox, root_definition, user_roles);
  }

  $: if (isMounted.value) jobs.selectJob(job);
  $: if (isMounted.value) {
    filetree.initialize(tree_items);
    filetree.handleMainScriptUpdate({ main_script_id });
  }
  $: if (isMounted.value) {
    workflowStore.update(active_workflow_id, workflow);
    if ($autoRezoom) designerControl.resetView();
  }
  $: if (isMounted.value) workflowParents.set(workflow_parents || []);
  $: isUserReadonly = workflowStore.isUserReadonly;

  const highlightRetryTimers = {};

  function paintHighlightedSteps(className, stepIds, stroke, fill, attempt = 0) {
    if (highlightRetryTimers[className]) {
      clearTimeout(highlightRetryTimers[className]);
      highlightRetryTimers[className] = null;
    }

    const wrapper = document.getElementById("workflow-designer-wrapper");
    if (!wrapper) return;

    wrapper.querySelectorAll(`.${className}`).forEach((el) => {
      el.classList.remove(className);
      el.querySelectorAll("rect").forEach((rect) => {
        rect.style.removeProperty("stroke");
        rect.style.removeProperty("stroke-width");
        rect.style.removeProperty("fill");
      });
    });

    const missing = [];

    for (const stepId of stepIds) {
      const stepEl = wrapper.querySelector(`[data-step-id="${stepId}"]`);
      if (!stepEl) {
        missing.push(stepId);
        continue;
      }

      stepEl.classList.add(className);
      stepEl.querySelectorAll("rect").forEach((rect) => {
        rect.style.setProperty("stroke", stroke, "important");
        rect.style.setProperty("stroke-width", "2px", "important");
        rect.style.setProperty("fill", fill, "important");
      });
    }

    // The designer's SVG may not have rendered yet — retry up to 2 seconds.
    if (missing.length > 0 && attempt < 20) {
      highlightRetryTimers[className] = setTimeout(
        () => paintHighlightedSteps(className, stepIds, stroke, fill, attempt + 1),
        100,
      );
    }
  }

  $: paintHighlightedSteps(
    "sqd-step-codegen-error",
    $codegenError?.id ? [$codegenError.id] : [],
    "#ef4444",
    "var(--color-surface-danger)",
  );

  $: paintHighlightedSteps(
    "sqd-step-codegen-warning",
    ($stepWarnings ?? []).map((step) => step.id).filter((id) => id !== $codegenError?.id),
    "#d97706",
    "var(--color-surface-warning)",
  );

  function codegenBannerMessage(error) {
    if (!error) return "";

    if (!error.structured && error.message) {
      return error.name ? `${error.message} ("${error.name}") — click to view` : error.message;
    }

    return error.name
      ? `Syntax error in "${error.name}" — click to view`
      : "Syntax error — click to view";
  }

  const isDirty = workflowStore.isDirty;
  const hasConnectionIssue = workflowStore.hasConnectionIssue;
  const autoSaveEnabled = workflowStore.autoSaveEnabled;
  const isAIOverlayOpen = ai.isOverlayOpen;
  const isAISidebarOpen = ai.isSidebarOpen;

  function handleWorkflowEvents(params) {
    if (!params) return null;
    const { events, isRecording } = params;
    if (isRecording) {
      execution.clearBeforeExecution();
    }
    processEventBatch(events, execution.processEvent);
  }

  function handleWorkflowFailedToStart(params) {
    if (!params) return null;
    const { reason } = params;
    execution.workflowFailedToStart(reason);
  }

  function handleAIResponse(params) {
    if (!params) return null;
    ai.processAIResponse(params);
  }

  function handleCodegenError(step) {
    console.log("[codegen_error] received:", step);
    if (!step) return;
    setCodegenError(step);
  }

  function handleCodegenErrorClear() {
    clearCodegenError();
  }

  function handleStepWarnings(payload) {
    setStepWarnings(payload?.steps ?? []);
  }

  onMount(() => {
    initializeStore(live, {
      project_id,
      toolbox,
      root_definition,
      workflow,
      ui_assets,
      user_roles,
      user_nickname,
      is_readonly,
      job,
      tree_items,
      project_name,
      main_script_id,
      feature_flags,
    });
    live.handleEvent("main_script_updated", filetree.handleMainScriptUpdate);
    live.handleEvent("workflow_events", handleWorkflowEvents);
    live.handleEvent("workflow_failed_to_start", handleWorkflowFailedToStart);
    live.handleEvent("ai_response", handleAIResponse);
    live.handleEvent("codegen_error", handleCodegenError);
    live.handleEvent("codegen_error_clear", handleCodegenErrorClear);
    live.handleEvent("step_warnings", handleStepWarnings);
    isMounted.set(true);
  });

  onDestroy(() => {
    live.removeHandleEvent(filetree.handleMainScriptUpdate);
    live.removeHandleEvent(handleWorkflowEvents);
    live.removeHandleEvent(handleWorkflowFailedToStart);
    live.removeHandleEvent(handleAIResponse);
    live.removeHandleEvent(handleCodegenError);
    live.removeHandleEvent(handleCodegenErrorClear);
    live.removeHandleEvent(handleStepWarnings);
    Object.values(highlightRetryTimers).forEach((timer) => clearTimeout(timer));
  });
</script>

{#if !hidden}
  <MenubarWorkflow hasActiveWorkflow={!!workflow} projectId={project_id} />

  <div
    class={cx("relative flex overflow-hidden automator-height-container", {
      "automator-mock-env": mock_env,
    })}
    id="workflow-designer-wrapper"
  >
    {#if $toolbar.show_code_pane}
      <SidebarWorkflow />
    {/if}
    <div class="relative w-full">
      {#if workflow}
        {#if $toolbar.show_code}
          <div class="absolute top-0 left-0 w-full h-full flex flex-col">
            <div class="relative flex-1 min-h-0">
              <CodeInput
                value={code}
                language="lua"
                isReadonly
                isSynced
                variant="standalone"
                jumpRequest={$codeViewJump}
              />
            </div>
            {#if $codegenError?.structured}
              <div class="shrink-0 max-h-64 overflow-auto border-t border-line-strong">
                <LuaError error={$codegenError.structured} onJumpToLine={viewInCode} />
              </div>
            {/if}
          </div>
        {:else if $toolbar.show_table}
          <PropTable />
        {:else}
          <!-- This fixed a bug where SequentialWorkflowDesigner doesn't work when not visible -->
          <SequentialWorkflowDesigner />

          <div
            class="absolute top-3 left-1/2 -translate-x-1/2 pr-80 z-20 flex flex-col items-center gap-2"
          >
            {#if $isDirty}
              {#if $autoSaveEnabled}
                <StatusBanner
                  variant={$hasConnectionIssue ? "warn" : "info"}
                  icon={$hasConnectionIssue ? ExclamationCircle : Sparkles}
                  message={$hasConnectionIssue
                    ? "Connection issue, trying to save..."
                    : "Saving changes..."}
                />
              {:else}
                <button
                  on:click={() => workflowStore.saveWorkflow()}
                  class="text-white font-bold py-1 px-8 rounded border border-red-800 bg-red-500 hover:bg-red-400 shadow-lg"
                >
                  {$hasConnectionIssue ? "Connection issue, trying again." : "Save"}
                </button>
              {/if}
            {/if}

            {#if !$alwaysReadwriteIfPossible && $isUserReadonly}
              <StatusBanner
                variant="warn"
                icon={ExclamationCircle}
                message="Read-only. Click lock to start editing."
              />
            {/if}

            {#if $codegenError}
              <button type="button" on:click={openErrorStep} class="block">
                <StatusBanner
                  variant="error"
                  icon={ExclamationCircle}
                  message={codegenBannerMessage($codegenError)}
                />
              </button>
            {/if}
          </div>
        {/if}
      {:else}
        <div class="w-full h-full flex flex-col items-center justify-center">
          <p class="text-xl font-semibold text-content-subtle mb-4">No workflow selected</p>
          <Icon src={LightBulb} class="mb-4 w-32 h-32 text-content-subtle" />
          <p class="text-sm text-content-subtle">
            To get started, select a workflow from the left sidebar or create a new one.
          </p>
        </div>
      {/if}
    </div>

    {#if $isAISidebarOpen}
      <AISidebar />
    {/if}
  </div>

  <AIOverlay isOpen={$isAIOverlayOpen} />
{/if}
